/**
 * Typer 作业 API 归一化（兼容旧扁平结构 + Swagger TeacherTyperAssignment + mappings）
 * @see https://api.andover.com.cn/swagger/index.html
 */

const STATUS_LABEL_MAP = {
  pending: '待完成',
  submitted: '已完成',
  graded: '已批改',
  done: '已完成',
  finished: '已完成'
}

export function formatTyperDateToMinute (value) {
  if (value == null || value === '') return '—'
  const s = String(value).trim()
  if (!s) return '—'
  const normalized = s.replace('T', ' ')
  if (normalized.length >= 16) return normalized.slice(0, 16)
  return normalized
}

export function resolveTyperAssignmentId (raw) {
  if (raw == null) return null
  return (
    raw.id ??
    raw.teacherTyperAssignmentId ??
    raw.assignmentId ??
    raw.typerAssignmentId ??
    null
  )
}

/** 从作业或 mapping 上取该学生的提交记录列表 */
export function listTyperAssignmentResults (raw, studentId) {
  if (raw == null) return []

  // 已是 mapping 节点
  if (
    Array.isArray(raw.results) &&
    raw.mappings == null &&
    (raw.studentId != null || raw.teacherTyperAssignmentId != null)
  ) {
    return raw.results
  }

  const sid = Number(studentId) || 0
  const mappings = raw.mappings ?? raw.typerAssignmentMappings
  if (Array.isArray(mappings) && mappings.length > 0) {
    const picked =
      sid > 0
        ? mappings.filter((m) => Number(m.studentId) === sid)
        : mappings
    const merged = picked.flatMap((m) =>
      Array.isArray(m.results) ? m.results : []
    )
    if (merged.length) return merged
  }

  const list =
    raw.results ??
    raw.teacherTyperAssignmentResults ??
    raw.typerAssignmentResults ??
    raw.assignmentResults ??
    raw.resultList ??
    []
  return Array.isArray(list) ? list : []
}

export function resolveTyperAiText (raw) {
  if (raw == null) return ''
  return String(
    raw.aIgeneratedText ??
      raw.AIgeneratedText ??
      raw.aiGeneratedText ??
      ''
  ).trim()
}

export function resolveTyperResultAssignmentId (raw) {
  if (raw == null) return null
  return raw.teacherTyperAssignmentId ?? raw.assignmentId ?? null
}

export function unwrapApiData (response) {
  const d = response?.data
  if (Array.isArray(d)) return d
  if (d?.data != null) {
    return Array.isArray(d.data) ? d.data : d.data
  }
  return d
}

export function resolveTyperBoundStudentId () {
  const rawTarget = localStorage.getItem('targetId')
  let id = parseInt(rawTarget, 10)
  if (Number.isFinite(id) && id > 0) return id
  const rawUser = localStorage.getItem('userId')
  id = parseInt(rawUser, 10)
  return Number.isFinite(id) && id > 0 ? id : 0
}

/** 与 Home / Header「Bound Student」一致：读取当前绑定的学生（非教师本人） */
export function readBoundStudentFromStorage (options = {}) {
  const { excludeSelf = true } = options
  const rawId = localStorage.getItem('targetId')
  if (rawId == null || String(rawId).trim() === '') return null
  const uid = Number(String(rawId).trim())
  if (!Number.isFinite(uid) || uid <= 0) return null
  if (excludeSelf) {
    const selfId = Number(String(localStorage.getItem('userId') || '').trim())
    if (Number.isFinite(selfId) && selfId > 0 && uid === selfId) return null
  }
  const cn = (localStorage.getItem('targetCNName') || '').trim()
  const un = (localStorage.getItem('targetName') || '').trim()
  return {
    userId: uid,
    userName: un || cn || String(uid),
    chineseName: cn || '—'
  }
}

export function normalizeTyperResultItem (raw, formatToMinute = formatTyperDateToMinute) {
  const fmt = formatToMinute
  return {
    ...raw,
    id:
      raw.id ??
      raw.resultId ??
      `${resolveTyperResultAssignmentId(raw) || ''}-${raw.createdAt || raw.createdDate || raw.submittedAt || ''}`,
    wpm: Number(raw.wpm ?? 0),
    typedCount: Number(raw.typedCount ?? raw.wordCount ?? 0),
    timeUsed: Number(raw.timeUsed ?? raw.duration ?? 0),
    passed: !!raw.passed,
    finished: !!raw.finished,
    typedContent: raw.typedContent != null ? String(raw.typedContent) : '',
    incorrectWords: raw.incorrectWords != null ? String(raw.incorrectWords) : '',
    createdAt: fmt(raw.createdAt ?? raw.submittedAt ?? raw.createdDate ?? '—')
  }
}

function deriveTyperAssignmentStatus (results, raw) {
  if (raw?.status || raw?.assignmentStatus) {
    const status = raw.status || raw.assignmentStatus
    return {
      status,
      statusLabel: raw.statusLabel || STATUS_LABEL_MAP[status] || status
    }
  }
  if (raw?.submittedAt || raw?.submittedDate) {
    return { status: 'submitted', statusLabel: '已完成' }
  }
  const list = Array.isArray(results) ? results : []
  if (!list.length) {
    return { status: 'pending', statusLabel: '待完成' }
  }
  if (list.some((r) => r.finished)) {
    return { status: 'submitted', statusLabel: '已完成' }
  }
  return { status: 'submitted', statusLabel: '已提交' }
}

/**
 * 新结构：一条 TeacherTyperAssignment 下可有多个 mappings（按 studentId）
 * 展开为「作业 + 当前学生 mapping」行，供列表展示
 */
export function expandTyperAssignmentsForStudent (rawList, studentId) {
  const sid = Number(studentId) || 0
  if (!Array.isArray(rawList)) return []

  const pairs = []
  for (const assignment of rawList) {
    const mappings = assignment.mappings ?? assignment.typerAssignmentMappings
    if (!Array.isArray(mappings) || mappings.length === 0) {
      if (
        sid > 0 &&
        assignment.studentId != null &&
        Number(assignment.studentId) !== sid
      ) {
        continue
      }
      pairs.push({ assignment, mapping: null })
      continue
    }
    for (const mapping of mappings) {
      if (sid > 0 && Number(mapping.studentId) !== sid) continue
      pairs.push({ assignment, mapping })
    }
  }
  return pairs
}

export function normalizeTyperAssignmentForDisplay (pair, options = {}) {
  const { assignment, mapping } = pair
  const fmt = options.formatToMinute || formatTyperDateToMinute
  const sid =
    Number(options.studentId) ||
    Number(mapping?.studentId) ||
    Number(assignment?.studentId) ||
    0

  const resultsRaw = mapping
    ? mapping.results
    : listTyperAssignmentResults(assignment, sid)
  const results = (Array.isArray(resultsRaw) ? resultsRaw : []).map((r) =>
    normalizeTyperResultItem(r, fmt)
  )

  const { status, statusLabel } = deriveTyperAssignmentStatus(results, assignment)

  const latestResult = [...results].sort((a, b) => {
    const ta = new Date(a.createdAt === '—' ? 0 : a.createdAt).getTime()
    const tb = new Date(b.createdAt === '—' ? 0 : b.createdAt).getTime()
    return tb - ta
  })[0]

  const bestWpm = results.length
    ? Math.max(...results.map((r) => Number(r.wpm) || 0))
    : null

  return {
    ...assignment,
    mappingId: mapping?.id ?? null,
    studentId: mapping?.studentId ?? assignment.studentId ?? sid,
    id: resolveTyperAssignmentId(assignment),
    title: assignment.title ?? assignment.assignmentTitle ?? '',
    status,
    statusLabel,
    wpm: assignment.wpm ?? 0,
    standardRate: assignment.standardRate ?? 0,
    countdown: assignment.countdown ?? 0,
    caseSensitive: !!(assignment.caseSensitive ?? assignment.isCaseSensitive),
    assignedAt: fmt(
      assignment.assignedAt ??
        assignment.assignedDate ??
        mapping?.createdDate ??
        assignment.createdDate ??
        '—'
    ),
    dueAt: fmt(assignment.dueAt ?? assignment.deadline ?? assignment.endDate ?? '—'),
    submittedAt: fmt(
      assignment.submittedAt ??
        assignment.submittedDate ??
        latestResult?.createdAt ??
        null
    ),
    bestWpm,
    bestAccuracy: assignment.bestAccuracy ?? assignment.bestAccurateRate ?? null,
    teacherComment: assignment.teacherComment ?? assignment.comment ?? '',
    originalText: assignment.originalText ?? '',
    AIgeneratedText: resolveTyperAiText(assignment),
    results
  }
}

/** 将 GET /TyperAssignment/student 等返回的列表转为表格行 */
export function normalizeTyperAssignmentList (rawList, options = {}) {
  const pairs = expandTyperAssignmentsForStudent(rawList, options.studentId)
  return pairs.map((pair) => normalizeTyperAssignmentForDisplay(pair, options))
}
